<template>
  <div class="row">
    <div class="col-md-7">
      <div class="card-header border-0 py-5">
        <h1>Create your job ads</h1>
        <vue-json-pretty
          v-if="errors"
          :data="errors"
          :deepCollapseChildren="true"
        ></vue-json-pretty>
        <v-tabs v-model="currentStep">
          <v-tab href="#Common">
            Common
          </v-tab>
          <v-tab
            v-for="postingRequirement in postingRequirements"
            :key="postingRequirement.id"
            :href="'#' + postingRequirement.name"
          >
            {{ postingRequirement.name }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="currentStep" class="pt-4">
          <v-tab-item value="Common">
            <b-form>
              <b-button
                class="small mb-4"
                size="sm"
                @click="useDummyCampaign()"
                variant="outline-secondary"
                >Use dummy data
              </b-button>
              <h3>Target Group</h3>
              <p>
                <label>Education Level</label>
                <b-form-select
                  v-model="campaign.targetGroup.educationLevel"
                  :options="educationLevels"
                  required
                ></b-form-select>
              </p>

              <p>
                <label>Seniority</label>
                <b-form-select
                  v-model="campaign.targetGroup.seniority"
                  :options="seniorities"
                  required
                ></b-form-select>
              </p>

              <p>
                <label
                  align="left"
                  :v-bind:for="campaign.targetGroup.jobCategory"
                  >Job function
                  <JobTitlesAndFunctions
                    return-type="node"
                    :key="123"
                    :mock-value="null"
                    options-source-endpoint="products/job-functions/"
                    v-model="campaign.targetGroup.jobCategory"
                    initialOptionsType="job-functions"
                    required
                  />
                </label>
              </p>

              <p>
                <label align="left" :v-bind:for="campaign.targetGroup.industry"
                  >Industry</label
                >
                <b-form-select
                  v-model="campaign.targetGroup.industry"
                  :options="allIndustries"
                  required
                ></b-form-select>
              </p>

              <h3>Recruiter Info</h3>
              <p>
                Recruiter is the person using the ATS to create the Campaign and
                it's generally part of the Company whose Id we request first.
              </p>
              <br />

              <p>
                <label>Full name</label>
                <b-form-input
                  v-model="campaign.recruiterInfo.name"
                  placeholder="Recruiter's full name"
                  required
                ></b-form-input>
              </p>

              <p>
                <label>Email Address</label>
                <b-form-input
                  type="email"
                  v-model="campaign.recruiterInfo.emailAddress"
                  placeholder="Recruiter's email address"
                  required
                ></b-form-input>
              </p>

              <br />

              <h3>Posting Details</h3>
              <p>
                Posting details represents the Job Posting information that's
                going to be published in the products selected in this request.
              </p>
              <br />
              <p>
                <label>Title</label>
                <b-form-input
                  v-model="campaign.postingDetails.title"
                  placeholder="The title of the posting across the different Channels where the posting is going to be published."
                  required
                >
                  <v-icon color="grey lighten-1">mdi-cart</v-icon>
                </b-form-input>
              </p>

              <p>
                <label>Description</label>
                <editor
                  v-model="campaign.postingDetails.description"
                  api-key="c1c6o9na5y7cicunede060zsw18e7lyov6lmt7ev54i9k8eo"
                  :init="{
                    height: 300,
                    menubar: true,
                    plugins: [
                      'advlist autolink lists link image charmap print preview anchor',
                      'searchreplace visualblocks code fullscreen',
                      'insertdatetime media table paste code help wordcount'
                    ],
                    toolbar:
                      'undo redo | formatselect | bold italic backcolor | \
           alignleft aligncenter alignright alignjustify | \
           bullist numlist outdent indent | removeformat | help'
                  }"
                />
              </p>

              <p>
                <label>Years of experience</label>
                <b-form-input
                  type="range"
                  min="0"
                  max="40"
                  v-model="campaign.postingDetails.yearsOfExperience"
                  required
                >
                </b-form-input>
                {{ campaign.postingDetails.yearsOfExperience }}
              </p>

              <p>
                <label>Employment type</label>
                <b-form-select
                  v-model="campaign.postingDetails.employmentType"
                  :options="employmentTypeOptions"
                  required
                ></b-form-select>
              </p>

              <p>
                <label
                  >Weekly working hours
                  <b-form-input
                    type="number"
                    placeholder="from"
                    v-model="campaign.postingDetails.weeklyWorkingHours.from"
                  ></b-form-input>
                  <b-form-input
                    type="number"
                    placeholder="to"
                    v-model="campaign.postingDetails.weeklyWorkingHours.to"
                    required
                  >
                  </b-form-input>
                </label>
              </p>

              <p>
                <label
                  >Salary Indication
                  <b-form-input
                    type="number"
                    placeholder="from"
                    v-model="
                      campaign.postingDetails.salaryIndication.range.from
                    "
                  ></b-form-input>
                  <b-form-input
                    type="number"
                    placeholder="to"
                    v-model="campaign.postingDetails.salaryIndication.range.to"
                    required
                  ></b-form-input>
                  <b-form-select
                    v-model="
                      campaign.postingDetails.salaryIndication.range.currency
                    "
                    :options="currency_list"
                  ></b-form-select>
                  Period
                  <b-form-select
                    v-model="campaign.postingDetails.salaryIndication.period"
                    :options="['yearly', 'monthly']"
                    required
                  ></b-form-select>
                </label>
              </p>

              <h3>Organization</h3>
              <p>
                The Organization for which the Job Posting is going to be
                directed. The company that's actually hiring a candidate.
              </p>
              <br />

              <p>
                <label>Name</label>
                <b-form-input
                  v-model="campaign.postingDetails.organization.name"
                  placeholder="Name of the Company"
                  required
                ></b-form-input>
              </p>
              <br />

              <p>
                <label>Company logo URL</label>
                <b-form-input
                  type="url"
                  v-model="campaign.postingDetails.organization.companyLogo"
                  placeholder="The company Logo that wants to be used on the posting. It has to be publicly available so it's picked up and used for the different publishing platforms"
                  required
                >
                </b-form-input>
              </p>

              <h3>Working Location</h3>
              <p>Working location as it should be published.</p>
              <br />

              <p>
                <label>Address Line 1</label>
                <b-form-input
                  v-model="campaign.postingDetails.workingLocation.addressLine1"
                  required
                >
                </b-form-input>
                <b-form-invalid-feedback id="input-live-feedback">
                  asd
                </b-form-invalid-feedback>
              </p>

              <p>
                <label>Address Line 2</label>
                <b-form-input
                  v-model="campaign.postingDetails.workingLocation.addressLine2"
                >
                </b-form-input>
              </p>

              <p>
                <label>Postcode</label>
                <b-form-input
                  v-model="campaign.postingDetails.workingLocation.postcode"
                  required
                >
                </b-form-input>
              </p>

              <p>
                <label>City</label>
                <b-form-input
                  v-model="campaign.postingDetails.workingLocation.city"
                  required
                >
                </b-form-input>
              </p>

              <p>
                <label>Country</label>
                <b-form-input
                  v-model="campaign.postingDetails.workingLocation.country"
                  required
                >
                </b-form-input>
              </p>

              <p>
                <label>
                  <b-form-checkbox
                    v-model="
                      campaign.postingDetails.workingLocation.allowsRemoteWork
                    "
                    size="lg"
                    >Allows remote work?
                  </b-form-checkbox>
                </label>
              </p>
              <br />

              <h3>URLs</h3>
              <p>
                <label>Job Page Url</label>
                <b-form-input
                  type="url"
                  v-model="campaign.postingDetails.jobPageUrl"
                  placeholder="Link to the page with the description of the job"
                  required
                >
                </b-form-input>
              </p>

              <p>
                <label>Application Url</label>
                <b-form-input
                  type="url"
                  v-model="campaign.postingDetails.applicationUrl"
                  placeholder="Link to the page where the candidate needs to be directed when applying for a position"
                  required
                >
                </b-form-input>
              </p>
            </b-form>
          </v-tab-item>
          <v-tab-item
            v-for="postingRequirement in postingRequirements"
            v-bind:key="postingRequirement.id"
            :value="postingRequirement.name"
          >
            <b-form>
              <h3>{{ postingRequirement.name }}</h3>
            </b-form>
            <b-form>
              <p>
                Custom Duration Days
                <b-form-input
                  v-model="postingDurationDays[postingRequirement.id]"
                  type="number"
                  placeholder="Optional custom duration days"
                ></b-form-input>
              </p>
            </b-form>
            <b-form>
              <FacetsForm
                v-if="postingRequirementsValues[postingRequirement.id]"
                :facets="postingRequirement.posting_requirements"
                :channel-id="postingRequirement.id"
                :credentials="postingRequirement.credentials"
                v-model="postingRequirementsValues[postingRequirement.id]"
              ></FacetsForm>
            </b-form>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </div>
    <div class="col-md-5">
      <div class="row">
        <div class="col-md-12">
          <Basket @products-update="onBasketProductsUpdate" :wallet="wallet">
          </Basket>
        </div>
      </div>
      <div class="row mt-5">
        <div class="col-md-12">
          <Payment
            ref="payment"
            v-on:ordercampaign="orderCampaign"
            v-on:instant-billing-success="clearBasketAndRedirect"
            :products="products"
          ></Payment>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BasketService } from "@/core/services/basket.service";
import Editor from "@tinymce/tinymce-vue";
import { HapiService } from "@/core/services/hapi.service";
import FacetsForm from "@/view/content/forms/FacetsForm";
import VueJsonPretty from "vue-json-pretty";
import "vue-json-pretty/lib/styles.css";
import { currency_list } from "@/assets/js/currencies";
import JobTitlesAndFunctions from "@/view/content/forms/JobTitlesAndFunctions";
import Basket from "@/view/pages/checkout/Basket";
import Payment from "@/view/components/checkout/Payment";
import { campaign } from "@/assets/js/dummyCampaign";
import CurrencyMixin from "@/view/mixins/CurrencyMixin";
import {
  RESET_CHECKOUT_STATE,
  UPDATE_CAMPAIGN,
  UPDATE_POSTING_REQUIMENTS_VALUE
} from "@/core/services/store/checkout.module";

export default {
  name: "Checkout",
  components: {
    Basket,
    Payment,
    editor: Editor,
    FacetsForm,
    VueJsonPretty,
    JobTitlesAndFunctions
  },
  mixins: [CurrencyMixin],
  data() {
    return {
      wallet: null,
      displayOptions: {},
      products: [],
      errors: null,
      contractsInBasket: [],
      postingRequirements: {},
      postingRequirementsValues: this.$store.state.checkout
        .postingRequirementsValues,
      postingDurationDays: this.$store.state.checkout.postingDurationDays,
      employmentTypeOptions: [
        { value: "permanent", text: "Permanent" },
        { value: "temporary", text: "Temporary" },
        { value: "fixed_term", text: "Fixed term" },
        {
          value: "fixed_term_with_option_for_permanent",
          text: "Fixed term with option for permanent"
        },
        { value: "freelance", text: "Freelance" },
        { value: "traineeship", text: "Traineeship" },
        { value: "internship", text: "Internship" }
      ],
      currency_list: currency_list,
      educationLevels: [],
      seniorities: [],
      allIndustries: [],
      campaign: {
        ...this.$store.state.checkout.campaign,
        companyId: this.$auth.user.sub,
        orderedProducts: BasketService.getProducts().map(
          value => value.productId
        )
      },
      currentStep: "common",
      tabsList: ["common"]
    };
  },
  methods: {
    async onBasketProductsUpdate(products) {
      this.products = products;
    },
    async orderCampaign(paymentMethod, walletId = null, poNumber = null) {
      const wallet = await this.$refs.payment.fetchWallet();
      if (
        wallet?.hasBillingDetails === false &&
        paymentMethod === "purchase_order"
      ) {
        return;
      }
      const request = Object.assign({}, this.campaign);
      request.paymentMethod = paymentMethod;
      request.poNumber = poNumber;
      request.currency = this.currency.code;
      if (walletId && paymentMethod !== "ats_managed") {
        request.walletId = walletId;
      }
      request.targetGroup = {
        ...request.targetGroup,
        jobCategory: this.parseJobFunctionNode(
          this.campaign.targetGroup.jobCategory
        )
      };
      const response = await HapiService.orderCampaign(request);
      if (response.status === 400 || response.status === 422) {
        await this.$set(this, "errors", response.data);
        window.scrollTo(0, 0);
        return false;
      }
      if (response.status === 201) {
        if (paymentMethod === "direct_charge") {
          this.$refs.payment.showBillingWidgetForCampaign(response.data);
        } else {
          await this.clearBasketAndRedirect();
        }
      }
    },
    async clearBasketAndRedirect() {
      BasketService.clearBasket();
      this.$store.dispatch(RESET_CHECKOUT_STATE);
      await this.$router.push({ name: "campaigns" });
    },
    errorFunction(error) {
      if (error === "" || error === null) {
        return null;
      } else if (error.length) {
        return false;
      }
    },
    errorMessage(error) {
      if (error.length) {
        return error.join("\n");
      }
    },
    arraysEqual(a, b) {
      if (a === b) return true;
      if (a == null || b == null) return false;
      if (a.length !== b.length) return false;

      // If you don't care about the order of the elements inside
      // the array, you should sort both arrays here.
      // Please note that calling sort on an array will modify that array.
      // you might want to clone your array first.

      for (var i = 0; i < a.length; ++i) {
        if (a[i] !== b[i]) return false;
      }
      return true;
    },
    async getPostingRequirements(contracts) {
      const promises = [];
      this.tabsList = ["Common"];
      const postingRequirementsValues = {};
      for (const contract of contracts) {
        promises.push(HapiService.retrieveChannelMoc(contract.channelId));
        postingRequirementsValues[contract.channelId] =
          this.postingRequirementsValues[contract.channelId] || {};
      }

      this.$store.dispatch(
        UPDATE_POSTING_REQUIMENTS_VALUE,
        postingRequirementsValues
      );
      this.postingRequirementsValues = postingRequirementsValues;
      Promise.all(promises).then(res => {
        this.postingRequirements = res.map(dataItem => {
          this.tabsList.push(dataItem.data.name);
          return dataItem.data;
        });
      });
    },
    parseJobFunctionNode(node) {
      return [{ vonqId: node.id, description: node.name }];
    },
    useDummyCampaign() {
      const newCampaign = { ...this.campaign, ...campaign };
      this.campaign = newCampaign;
      this.$store.dispatch(UPDATE_CAMPAIGN, newCampaign);
      this.$forceUpdate();
    }
  },
  async mounted() {
    const self = this;
    this.displayOptions = BasketService.getDisplayOptions();
    setInterval(function() {
      self.campaign.orderedProducts = BasketService.getProducts().map(
        val => val.productId
      );
      self.contractsInBasket = BasketService.getProducts().filter(
        value => value.contractId !== null
      );
      self.campaign.orderedProductsSpecs = BasketService.getProducts()
        .filter(val => val.contractId !== null)
        .map(function(val) {
          return {
            productId: val.productId,
            utm: "utm_campaign=some_campaign",
            contractId: val.contractId,
            postingDurationDays:
              self.postingDurationDays[val.channelId] || undefined,
            postingRequirements: self.postingRequirementsValues[val.channelId]
          };
        });
    }, 500);

    const contractIds = this.contractsInBasket.map(
      value => value["contractId"]
    );

    const educationResponse = await HapiService.listEducationLevels();
    this.educationLevels = educationResponse.data.map(function(value) {
      return {
        text: value.name[1].value,
        value: [{ description: value.name[1].value, vonqId: value.id }]
      };
    });

    const senioritiesResponse = await HapiService.listSeniorities();
    this.seniorities = senioritiesResponse.data.map(function(value) {
      return {
        text: value.name[1].value,
        value: [{ description: value.name[1].value, vonqId: value.id }]
      };
    });
    const industryResponse = await HapiService.listIndustries();
    this.allIndustries = industryResponse.data.map(function(value) {
      return {
        text: value.name,
        value: [{ description: value.name, vonqId: value.id }]
      };
    });

    this.$on("orderCampaign", () => {
      this.orderCampaign();
    });
    this.wallet = await this.$refs.payment.fetchWallet();
  },
  watch: {
    async contractsInBasket(newVal, oldVal) {
      const justContractIds = val => val.contractId;

      if (
        !this.arraysEqual(
          newVal.map(justContractIds),
          oldVal.map(justContractIds)
        )
      ) {
        await this.getPostingRequirements(newVal);
      }
    }
  }
};
</script>
